/**
 * Order Sheet — Bottom sheet modal for order summary and placement.
 * Sections: order lines → delivery mode → payment mode → totals → confirm button
 * Success state shows ETA and confirmation message.
 */

import { getCurrentLanguage } from '../../../i18n';
import type { CartItem, OrderMode, PaymentMode } from '../types';
import { SERVICE_CHARGE_PCT } from '../types';

const L: Record<string, Record<string, string>> = {
  th: {
    title: 'ออเดอร์ของคุณ', deliveryTitle: 'รูปแบบการรับอาหาร',
    deliverRoom: '🛏️ ส่งมาที่ห้อง', dineIn: '🍽️ ทานที่ร้าน', takeawayDelivery: '🛍️ สั่งกลับบ้าน',
    takeawayHint: 'รับอาหารที่เคาน์เตอร์ของร้านอาหาร', tableLocked: 'สแกนสั่งจาก QR Code',
    roomLabel: 'หมายเลขห้องพัก *', roomPlaceholder: 'เช่น 204, 312',
    tableLabel: 'หมายเลขโต๊ะ *', tablePlaceholder: 'เช่น T-05, โต๊ะ 4',
    payTitle: 'วิธีชำระเงิน', chargeRoom: '🧾 เก็บเข้าบิลห้อง', payTbl: '💳 ชำระที่โต๊ะ / แคชเชียร์',
    subtotal: 'ยอดรวม', svc: 'ค่าบริการ', total: 'ยอดรวมทั้งหมด',
    place: 'ยืนยันการสั่งอาหาร', success: '✅ รับออเดอร์แล้ว!',
    eta: '⏱ เวลาโดยประมาณ ~25–35 นาที', orderMoreBtn: '🛒 สั่งอาหารเพิ่ม',
    viewOrderHistoryBtn: '📋 ดูสถานะและประวัติออเดอร์',
  },
  en: {
    title: 'Your Order', deliveryTitle: 'Delivery Option',
    deliverRoom: '🛏️ Deliver to room', dineIn: '🍽️ Dine in', takeawayDelivery: '🛍️ Takeaway / Pickup',
    takeawayHint: 'Pickup at restaurant counter', tableLocked: 'Ordered via QR Code',
    roomLabel: 'Room Number *', roomPlaceholder: 'e.g. 204, 312',
    tableLabel: 'Table Number *', tablePlaceholder: 'e.g. T-05, Table 4',
    payTitle: 'Payment Method', chargeRoom: '🧾 Charge to room', payTbl: '💳 Pay at table / cashier',
    subtotal: 'Subtotal', svc: 'Service charge', total: 'Grand Total',
    place: 'Place Order', success: '✅ Order Received!',
    eta: '⏱ Estimated time ~25–35 minutes', orderMoreBtn: '🛒 Order More',
    viewOrderHistoryBtn: '📋 View Order Status',
  },
  zh: {
    title: '您的订单', deliveryTitle: '用餐方式',
    deliverRoom: '🛏️ 送到房间', dineIn: '🍽️ 堂食', takeawayDelivery: '🛍️ 外带 / 自提',
    takeawayHint: '在餐厅前台自取', tableLocked: '已扫桌码',
    roomLabel: '房间号 *', roomPlaceholder: '如 204',
    tableLabel: '桌号 *', tablePlaceholder: '如 T-05',
    payTitle: '付款方式', chargeRoom: '🧾 记房费', payTbl: '💳 在餐桌 / 前台结账',
    subtotal: '小计', svc: '服务费', total: '总计',
    place: '下单', success: '✅ 已收到订单！',
    eta: '⏱ 预计时间约25–35分钟', orderMoreBtn: '🛒 继续点餐',
    viewOrderHistoryBtn: '📋 查看订单状态',
  },
  ru: {
    title: 'Ваш заказ', deliveryTitle: 'Способ получения',
    deliverRoom: '🛏️ В номер', dineIn: '🍽️ В зале', takeawayDelivery: '🛍️ На вынос',
    takeawayHint: 'Заберите у стойки ресторана', tableLocked: 'Заказ по QR-коду',
    roomLabel: 'Номер комнаты *', roomPlaceholder: 'напр. 204',
    tableLabel: 'Номер столика *', tablePlaceholder: 'напр. T-05',
    payTitle: 'Способ оплаты', chargeRoom: '🧾 На счёт номера', payTbl: '💳 Оплата у столика / кассы',
    subtotal: 'Подытог', svc: 'Сервисный сбор', total: 'Итого',
    place: 'Оформить заказ', success: '✅ Заказ принят!',
    eta: '⏱ Ориентировочное время ~25–35 минут', orderMoreBtn: '🛒 Заказать ещё',
    viewOrderHistoryBtn: '📋 Статус заказов',
  },
  ja: {
    title: 'ご注文内容', deliveryTitle: 'お届け方法',
    deliverRoom: '🛏️ お部屋へデリバリー', dineIn: '🍽️ 店内でお食事', takeawayDelivery: '🛍️ テイクアウト',
    takeawayHint: 'レストランのカウンターでお受け取りください', tableLocked: 'QRコード注文',
    roomLabel: 'お部屋番号 *', roomPlaceholder: '例: 204',
    tableLabel: 'テーブル番号 *', tablePlaceholder: '例: T-05',
    payTitle: 'お支払い方法', chargeRoom: '🧾 客室付け', payTbl: '💳 テーブル / レジでお支払い',
    subtotal: '小計', svc: 'サービス料', total: '合計',
    place: '注文を確定', success: '✅ ご注文を受け付けました！',
    eta: '⏱ 所要時間の目安 約25–35分', orderMoreBtn: '🛒 追加注文する',
    viewOrderHistoryBtn: '📋 注文履歴を確認',
  },
  ko: {
    title: '주문 내역', deliveryTitle: '수령 방법',
    deliverRoom: '🛏️ 객실로 배달', dineIn: '🍽️ 매장 식사', takeawayDelivery: '🛍️ 포장 (테이크아웃)',
    takeawayHint: '레스토랑 카운터에서 수령해 주세요', tableLocked: 'QR 주문 완료',
    roomLabel: '객실 번호 *', roomPlaceholder: '예: 204',
    tableLabel: '테이블 번호 *', tablePlaceholder: '예: T-05',
    payTitle: '결제 방법', chargeRoom: '🧾 룸 차지', payTbl: '💳 테이블 / 계산대에서 결제',
    subtotal: '소계', svc: '서비스 요금', total: '합계',
    place: '주문하기', success: '✅ 주문이 접수되었습니다!',
    eta: '⏱ 예상 시간 ~25–35분', orderMoreBtn: '🛒 더 주문하기',
    viewOrderHistoryBtn: '📋 주문 현황 확인',
  },
};

export function getOrderSheetL(key: string): string {
  const lang = getCurrentLanguage();
  return (L[lang] || L.en)[key] || (L.en)[key] || key;
}

function modeBtn(id: string, label: string, active: boolean, handler: string): string {
  return `<button id="${id}" onclick="${handler}" style="
    flex:1;padding:10px 8px;border:1.5px solid ${active ? '#00B900' : '#e0e0e0'};
    border-radius:8px;background:${active ? '#E8F5E8' : '#fff'};
    color:${active ? '#00B900' : '#555'};font-size:13px;font-weight:${active ? '700' : '500'};
    cursor:pointer;touch-action:manipulation;transition:all 0.15s;
  ">${label}</button>`;
}

export interface OrderSheetOptions {
  allowRoomDelivery?: boolean;
  allowDineIn?: boolean;
  allowTakeaway?: boolean;
  allowChargeToRoom?: boolean;
  allowPayAtTable?: boolean;
  isTableLocked?: boolean;
  serviceChargePct?: number;
}

export function renderLocationContainerContent(
  orderMode: OrderMode,
  initialRoomNumber: string = '',
  initialTableNumber: string = '',
  isLocked: boolean = false
): string {
  if (orderMode === 'takeaway') {
    return `<div style="padding:11px 13px;background:#f0fdf4;border:1.5px solid #bbf7d0;border-radius:8px;font-size:13px;color:#166534;font-weight:600;display:flex;align-items:center;gap:6px;">
      <span>🛍️</span> <span>${getOrderSheetL('takeawayHint')}</span>
    </div>`;
  }
  if (orderMode === 'dine' && isLocked) {
    return `<div>
      <label style="display:block;font-size:12px;font-weight:700;color:#15803d;margin-bottom:5px;">
        ${getOrderSheetL('tableLabel')} (${getOrderSheetL('tableLocked')})
      </label>
      <div style="padding:10px 12px;border:1.5px solid #86efac;border-radius:8px;font-size:15px;font-weight:700;background:#f0fdf4;color:#166534;display:flex;align-items:center;justify-content:space-between;">
        <span>🍽️ โต๊ะ ${initialTableNumber}</span>
        <span style="font-size:11px;font-weight:600;background:#dcfce7;color:#15803d;padding:2px 8px;border-radius:6px;">LOCKED</span>
      </div>
    </div>`;
  }
  return `<div>
    <label id="sheet-location-label" style="display:block;font-size:12px;font-weight:700;color:#475569;margin-bottom:5px;">
      ${orderMode === 'room' ? getOrderSheetL('roomLabel') : getOrderSheetL('tableLabel')}
    </label>
    <input
      id="sheet-location-input"
      type="text"
      value="${orderMode === 'room' ? initialRoomNumber : initialTableNumber}"
      placeholder="${orderMode === 'room' ? getOrderSheetL('roomPlaceholder') : getOrderSheetL('tablePlaceholder')}"
      oninput="window.setOrderLocation?.(this.value)"
      style="width:100%;padding:10px 12px;border:1.5px solid #cbd5e1;border-radius:8px;font-size:14px;box-sizing:border-box;background:#f8fafc;"
    />
  </div>`;
}

export function renderOrderSheet(
  cart: Map<string, CartItem>,
  orderMode: OrderMode,
  payMode: PaymentMode,
  showSuccess: boolean,
  initialRoomNumber: string = '',
  initialTableNumber: string = '',
  options: OrderSheetOptions = {}
): string {
  const lang = getCurrentLanguage();
  const allowRoom = options.allowRoomDelivery ?? false;
  const allowDine = options.allowDineIn ?? true;
  const allowTakeaway = options.allowTakeaway ?? true;
  const allowChargeRoom = options.allowChargeToRoom ?? false;
  const allowPayTable = options.allowPayAtTable ?? true;
  const isLocked = options.isTableLocked ?? false;
  const svcRate = options.serviceChargePct ?? SERVICE_CHARGE_PCT;

  const subtotal = [...cart.values()].reduce((s, c) => s + c.item.price * c.qty, 0);
  const svc = Math.round((subtotal * svcRate) / 100);
  const total = subtotal + svc;

  const lines = [...cart.values()].map((ci) => {
    const name = (ci.item.name as Record<string, string>)[lang] || ci.item.name.en;
    const safeId = ci.item.id.replace(/[^a-zA-Z0-9]/g, '_');
    return `
      <div id="sheet-row-${safeId}" style="display:flex;align-items:center;gap:10px;padding:10px 0;border-bottom:1px solid #f0f0f0;">
        <div style="flex:1;font-size:14px;color:#1a1a1a;">${name}</div>
        <div style="display:flex;align-items:center;gap:6px;">
          <button onclick="window.decreaseCart?.('${ci.item.id}')" aria-label="Decrease" style="
            min-width:32px;min-height:32px;border:1.5px solid #e0e0e0;border-radius:6px;
            background:#fff;color:#00B900;font-size:16px;font-weight:700;cursor:pointer;touch-action:manipulation;">−</button>
          <span id="sheet-qty-${safeId}" style="min-width:24px;text-align:center;font-weight:700;">${ci.qty}</span>
          <button onclick="window.addToCart?.('${ci.item.id}')" aria-label="Increase" style="
            min-width:32px;min-height:32px;border:1.5px solid #e0e0e0;border-radius:6px;
            background:#fff;color:#00B900;font-size:16px;font-weight:700;cursor:pointer;touch-action:manipulation;">+</button>
        </div>
        <div id="sheet-price-${safeId}" style="min-width:60px;text-align:right;font-size:14px;font-weight:600;color:#1a1a1a;">฿${(ci.item.price * ci.qty).toLocaleString()}</div>
      </div>
    `;
  }).join('');

  const body = showSuccess
    ? `<div style="text-align:center;padding:32px 16px;">
        <div style="font-size:40px;margin-bottom:12px;">🎉</div>
        <div style="font-size:18px;font-weight:700;color:#00B900;margin-bottom:8px;">${getOrderSheetL('success')}</div>
        <div style="font-size:14px;color:#666;margin-bottom:24px;">${getOrderSheetL('eta')}</div>
        <div style="display:flex;gap:10px;justify-content:center;flex-wrap:wrap;">
          <button onclick="window.closeOrderSheet?.()" style="
            flex:1;min-width:140px;padding:13px 18px;background:#f1f5f9;color:#334155;border:1.5px solid #cbd5e1;border-radius:10px;
            font-size:14px;font-weight:700;cursor:pointer;touch-action:manipulation;">
            ${getOrderSheetL('orderMoreBtn')}
          </button>
          <button onclick="window.viewOrderHistory?.()" style="
            flex:1;min-width:140px;padding:13px 18px;background:#00B900;color:#fff;border:none;border-radius:10px;
            font-size:14px;font-weight:700;cursor:pointer;touch-action:manipulation;box-shadow:0 4px 12px rgba(0,185,0,0.25);">
            ${getOrderSheetL('viewOrderHistoryBtn')}
          </button>
        </div>
      </div>`
    : `
      <div id="sheet-lines-container" style="padding:0 16px;">${lines}</div>
      <div style="padding:12px 16px 0;">
        <div style="font-size:13px;font-weight:700;color:#333;margin-bottom:8px;">${getOrderSheetL('deliveryTitle')}</div>
        <div style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap;">
          ${allowDine ? modeBtn('sheet-mode-dine', getOrderSheetL('dineIn'), orderMode === 'dine', "window.setOrderMode?.('dine')") : ''}
          ${allowTakeaway ? modeBtn('sheet-mode-takeaway', getOrderSheetL('takeawayDelivery'), orderMode === 'takeaway', "window.setOrderMode?.('takeaway')") : ''}
          ${allowRoom ? modeBtn('sheet-mode-room', getOrderSheetL('deliverRoom'), orderMode === 'room', "window.setOrderMode?.('room')") : ''}
        </div>

        <div id="sheet-location-container" style="margin-bottom:14px;">
          ${renderLocationContainerContent(orderMode, initialRoomNumber, initialTableNumber, isLocked)}
        </div>

        <div style="font-size:13px;font-weight:700;color:#333;margin-bottom:8px;">${getOrderSheetL('payTitle')}</div>
        <div style="display:flex;gap:8px;margin-bottom:16px;">
          ${allowPayTable ? modeBtn('sheet-pay-tbl', getOrderSheetL('payTbl'), payMode === 'tbl', "window.setPayMode?.('tbl')") : ''}
          ${allowChargeRoom ? modeBtn('sheet-pay-room', getOrderSheetL('chargeRoom'), payMode === 'room', "window.setPayMode?.('room')") : ''}
        </div>
        <div style="border-top:1px solid #f0f0f0;padding-top:12px;">
          <div style="display:flex;justify-content:space-between;font-size:14px;color:#666;margin-bottom:4px;">
            <span>${getOrderSheetL('subtotal')}</span><span id="sheet-subtotal">฿${subtotal.toLocaleString()}</span>
          </div>
          <div style="display:flex;justify-content:space-between;font-size:14px;color:#666;margin-bottom:8px;">
            <span>${getOrderSheetL('svc')} (${svcRate}%)</span><span id="sheet-svc">฿${svc.toLocaleString()}</span>
          </div>
          <div style="display:flex;justify-content:space-between;font-size:16px;font-weight:700;color:#1a1a1a;">
            <span>${getOrderSheetL('total')}</span><span id="sheet-total">฿${total.toLocaleString()}</span>
          </div>
        </div>
        <button id="sheet-submit-btn" onclick="window.submitOrder?.()" style="
          display:block;width:100%;margin-top:16px;padding:14px;
          background:#00B900;color:#fff;border:none;border-radius:10px;
          font-size:16px;font-weight:700;cursor:pointer;touch-action:manipulation;">
          ${getOrderSheetL('place')}
        </button>
      </div>
    `;

  return `
    <div id="order-sheet-overlay" onclick="window.closeOrderSheet?.()" style="
      position:fixed;inset:0;background:rgba(0,0,0,0.45);z-index:10000;
      display:flex;align-items:flex-end;
    ">
      <div onclick="event.stopPropagation()" style="
        width:100%;max-height:85vh;background:#fff;
        border-radius:20px 20px 0 0;
        padding-bottom:env(safe-area-inset-bottom,0px);
        overflow-y:auto;-webkit-overflow-scrolling:touch;
      ">
        <div style="display:flex;align-items:center;justify-content:space-between;padding:16px 16px 12px;border-bottom:1px solid #f0f0f0;">
          <span style="font-size:17px;font-weight:700;color:#1a1a1a;">${getOrderSheetL('title')}</span>
          <button onclick="window.closeOrderSheet?.()" style="
            width:32px;height:32px;border:none;background:#f0f0f0;border-radius:50%;
            font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;">✕</button>
        </div>
        ${body}
        <div style="height:16px;"></div>
      </div>
    </div>
  `;
}
